import { DwcaExtensionLabel } from '@/components/filters/displayNames';
import { Message } from '@/components/message';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/largeCard';
import { ArticleContainer } from '@/routes/resource/key/components/articleContainer';
import { ArticleTextContainer } from '@/routes/resource/key/components/articleTextContainer';
import { MdDownload } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDatasetKeyLoaderData } from '.';
import { useDatasetKeyContext } from './datasetKey';

export function DatasetKeyDownload() {
  const { dataset } = useDatasetKeyLoaderData().data;
  const { contentMetrics } = useDatasetKeyContext();
  const occurrenceCount = contentMetrics?.occurrenceSearch?.documents?.total ?? 0;

  const fullPredicate = {
    type: 'equals',
    key: 'DATASET_KEY',
    value: dataset.key,
  };

  const sourceDownloadEndpoint = dataset?.endpoints?.find(function (e) {
    return ['DWC_ARCHIVE', 'CAMTRAP_DP'].includes(e.type);
  });

  const dataPackageEndpoint = dataset?.endpoints?.find((e) => e.type === 'DWC_DP');

  return (
    <ArticleContainer className="g-bg-slate-100 g-pt-4">
      <ArticleTextContainer className="g-max-w-screen-xl">
        <div className="g-flex g-flex-wrap -g-mx-4">
          {occurrenceCount > 0 && (
            <div>
              <Card className="g-flex-none md:g-w-96 g-max-w-full g-mx-4 g-mb-4">
                <CardHeader>
                  <CardTitle>
                    <FormattedMessage id="dataset.processedOccurrences" />
                  </CardTitle>
                  <CardDescription className="g-text-base g-prose g-pt-6">
                    <Message id="dataset.processedOccurrencesDescription" />
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="default" className="g-text-center g-w-full" asChild>
                    <a
                      className="g-text-white"
                      href={`${
                        import.meta.env.PUBLIC_GBIF_ORG
                      }/occurrence/download/request?predicate=${encodeURIComponent(
                        JSON.stringify(fullPredicate)
                      )}#create`}
                    >
                      <FormattedMessage id="download.continueToGBIF" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          )}
          {dataset?.checklistBankDataset?.key && (
            <div>
              <Card className="g-flex-none md:g-w-96 g-max-w-full g-mx-4 g-mb-4">
                <CardHeader>
                  <CardTitle>
                    <FormattedMessage id="dataset.checklistBankDownload" />
                  </CardTitle>
                  <CardDescription className="g-text-base g-prose g-pt-6">
                    <Message id="dataset.checklistBankDownloadDescription" />
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="default" className="g-text-center g-w-full" asChild>
                    <a
                      href={`${import.meta.env.PUBLIC_CHECKLIST_BANK_WEBSITE}/dataset/gbif-${
                        dataset.key
                      }/download`}
                      rel="noopener noreferrer"
                      className="g-text-white"
                    >
                      <FormattedMessage id="phrases.continue" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          )}
          {dataPackageEndpoint && (
            <div>
              <Card className="g-flex-none md:g-w-96 g-max-w-full g-mx-4 g-mb-4">
                <CardHeader>
                  <CardTitle>
                    <FormattedMessage id="dataset.dataPackage" defaultMessage="Data package" />
                  </CardTitle>
                  <CardDescription className="g-text-base g-prose g-pt-6">
                    <Message id="dataset.dataPackageDescription" />
                    <div>
                      <FormattedMessage id="downloadKey.format" defaultMessage="Format" />:{' '}
                      <FormattedMessage
                        id={`enums.endpointType.${dataPackageEndpoint.type}`}
                        defaultMessage={dataPackageEndpoint.type}
                      />
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="default" className="g-text-center g-w-full" asChild>
                    <a
                      href={`${dataPackageEndpoint.url}`}
                      rel="noopener noreferrer"
                      className="g-text-white"
                    >
                      <FormattedMessage id="phrases.download" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          )}
          {sourceDownloadEndpoint && (
            <div>
              <Card className="g-flex-none md:g-w-96 g-max-w-full g-mx-4 g-mb-4">
                <CardHeader>
                  <CardTitle>
                    <FormattedMessage id="dataset.originalArchive" />
                  </CardTitle>
                  <CardDescription className="g-text-base g-prose g-pt-6">
                    <Message id="dataset.originalArchiveDescription" />
                    <div>
                      <FormattedMessage
                        id={`downloadKey.${sourceDownloadEndpoint.type}`}
                        defaultMessage="Format"
                      />
                      :{' '}
                      <FormattedMessage
                        id={`enums.endpointType.${sourceDownloadEndpoint.type}`}
                        defaultMessage={sourceDownloadEndpoint.type}
                      />
                    </div>
                    {dataset?.dwca?.extensions && dataset.dwca.extensions.length > 0 && (
                      <div>
                        <FormattedMessage id="dataset.extensions" defaultMessage="Extensions" />:
                        <ul className="g-list-none g-mt-1">
                          {dataset.dwca.extensions.map((ext) => (
                            <li key={ext} className="g-rounded g-bg-slate-100 g-px-2 g-py-1 g-mb-1">
                              {ext && <DwcaExtensionLabel id={ext} />}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardFooter>
                  <Button variant="default" className="g-text-center g-w-full" asChild>
                    <a
                      href={`${sourceDownloadEndpoint.url}`}
                      rel="noopener noreferrer"
                      className="g-text-white"
                    >
                      <FormattedMessage id="phrases.download" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          )}
        </div>
        <div className="g-mt-4 g-text-slate-600">
          <a
            className="g-underline g-text-inherit g-flex-inline g-items-center"
            href={`${import.meta.env.PUBLIC_API_V1}/dataset/${dataset.key}/document`}
          >
            <MdDownload />
            <span className="g-ms-2">
              <FormattedMessage id="dataset.annotatedEml" />
            </span>
          </a>
        </div>
      </ArticleTextContainer>
    </ArticleContainer>
  );
}
